import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { ScheduleType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import FormScheduleComponent from 'webapp/components/form/schedule';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type BoardShare from 'webapp/models-custom/board-share';

export default class BoardPopupAutoScheduleSnapshotComponent extends PopupInnerModalComponent<PopupData.BoardShare.AutoScheduleSnapshot> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('onboarding') declare onboardingService: Service.Onboarding;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/board-shares')
	declare mmBoardSharesService: Service.ModelManager.BoardShares;

	@tracked model: BoardShare;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const props = {
			boardId: Number(this.boardId),
			recipients: [this.currentUserService.model!.email],
			includeLink: true,
			scheduleType: ScheduleType.WEEKLY,
			scheduleHourOfDay: 9,
			scheduleDayOfWeek: [1],
			scheduleDayOfMonth: null,
		};
		this.model = this.mmBoardSharesService.createNewModel(props);

		let eventName;
		if (this.isForTemplateOnboarding) {
			eventName = TrackingEvent.PROMOTE_SCHEDULED_SNAPSHOT_TEMPLATE;
		} else {
			eventName = this.isForFree
				? TrackingEvent.PROMOTE_SCHEDULED_SNAPSHOT_FREE
				: TrackingEvent.PROMOTE_SCHEDULED_SNAPSHOT;
		}

		this.analyticsService.event(eventName);

		next(() => {
			this.currentUserService.markBoardSchedulePopupSeen();
		});
	}

	get boardId() {
		return this.popupData.boardId;
	}

	get isForFree() {
		return !!this.popupData.isForFree;
	}

	get isForTemplateOnboarding() {
		// used for experiment 2
		return this.popupData.isForTemplateOnboarding;
	}

	@action
	schedule(): void {
		if (this.submitInProgress) {
			return;
		}

		const model = this.model;

		if (!model?.validate()) {
			return;
		}

		this.submitInProgress = true;

		this.analyticsService.event(TrackingEvent.SHARE_NEW_SNAPSHOT, {
			format: 'JPG',
			type: 'databoard',
			delivery: 'email',
			frequency: model.schedule.typeText.toLowerCase(),
			name: model.boardObject?.name,
			datasources: model.boardObject?.specificSourceTypesKeys,
		});

		model.cleanupUnusedScheduleValues();

		model
			.save()
			.then(
				() => {
					this.infolayerService.ok('Your snapshot has been successfully scheduled.');

					if (this.isForTemplateOnboarding) {
						this.onboardingService.triggerTransitionToPublicTemplatesOnSnapshotScheduled();
					}

					this.close();
				},
				(errorData) => {
					throw errorData;
				},
			)
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}
	<template>
		<div class="content">

			{{#if this.model.boardObject.imageMedium}}
				<div class="frame">
					<img src={{this.model.boardObject.imageMedium}} alt />
				</div>
			{{else}}
				<img
					class="example"
					src="/images/placeholders/placeholder-boards.png"
					height="250"
					alt
				/>
			{{/if}}

			<h3>
				One last thing... how often
				<br />
				do you want to monitor this data?
			</h3>

			<p>
				You've built a great dashboard, let's make sure you use it!
				<br />
				Schedule a time for a screenshot of your dashboard to be delivered to you via email.
			</p>

			<FormScheduleComponent
				@model={{this.model}}
				@showOneOff={{this.model.isNew}}
				@submitInProgress={{this.submitInProgress}}
			/>

			<div class="controls2">
				<div class="controls2__left">
					<SubmitButtonComponent
						@text="No thanks, I'm good"
						@theme="grey-empty"
						@onTrigger={{this.close}}
					/>
				</div>
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Schedule now"
						@extraClass="save"
						@size="big"
						@submitInProgress={{this.submitInProgress}}
						@onTrigger={{this.schedule}}
					/>
				</div>
			</div>

		</div>
	</template>
}
